"use client";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Contingency, Offer } from "@/schemas/offer";
import { InfoIcon } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { PriceInput } from "./price-input";

interface ContingenciesSectionProps {
  form: UseFormReturn<Offer>;
  onFormChange: <K extends keyof Offer>(field: K, value: Offer[K]) => void;
}

const CONTINGENCY_FIELDS = [
  {
    type: "inspection",
    label: "Inspection Contingency",
    description: "Period to complete property inspection",
    periodField: "inspection_period",
    defaultPeriod: 10,
  },
  {
    type: "appraisal",
    label: "Appraisal Contingency",
    description: "Period to complete property appraisal",
    periodField: "appraisal_period",
    defaultPeriod: 21,
  },
  {
    type: "financing",
    label: "Financing Contingency",
    description: "Period to secure financing",
    periodField: "financing_period",
    defaultPeriod: 30,
  },
  {
    type: "title",
    label: "Title Contingency",
    description: "Period for title review",
    periodField: "title_period",
    defaultPeriod: 15,
  },
  {
    type: "sale_of_home",
    label: "Sale of Home Contingency",
    description: "Period to sell current home",
    periodField: "sale_of_home_period",
    defaultPeriod: 60,
  },
  {
    type: "insurance",
    label: "Insurance Contingency",
    description: "Period to secure insurance",
    periodField: "insurance_period",
    defaultPeriod: 14,
  },
  {
    type: "hoa",
    label: "HOA Contingency",
    description: "Period to review HOA documents",
    periodField: "hoa_period",
    defaultPeriod: 7,
  },
] as const;

export function ContingenciesSection({
  form,
  onFormChange,
}: ContingenciesSectionProps) {
  const contingencies = form.watch("contingencies") || [];

  // Type the handleContingencyToggle function properly
  const handleContingencyToggle = (type: Contingency, isEnabled: boolean) => {
    const newContingencies: Contingency[] = isEnabled
      ? [...contingencies, type]
      : contingencies.filter((c) => c !== type);

    form.setValue("contingencies", newContingencies);
    onFormChange("contingencies", newContingencies);

    // Reset period field if contingency is disabled
    const contingencyConfig = CONTINGENCY_FIELDS.find((f) => f.type === type);
    if (contingencyConfig?.periodField) {
      if (!isEnabled) {
        form.setValue(contingencyConfig.periodField, null);
        onFormChange(contingencyConfig.periodField, null);
      } else {
        // Set default period when enabling
        form.setValue(
          contingencyConfig.periodField,
          contingencyConfig.defaultPeriod,
        );
        onFormChange(
          contingencyConfig.periodField,
          contingencyConfig.defaultPeriod,
        );
      }
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Contingencies</h3>
            <p className="text-sm text-muted-foreground">
              Select and configure the contingencies for your offer
            </p>
          </div>

          <div className="space-y-6">
            {CONTINGENCY_FIELDS.map(
              ({ type, label, description, periodField, defaultPeriod }) => (
                <div
                  key={type}
                  className="flex items-start justify-between gap-4 p-4 rounded-lg border bg-card"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold">{label}</div>
                      {type === "inspection" && (
                        <Badge variant="secondary">Common</Badge>
                      )}
                      {type === "financing" && (
                        <Badge variant="secondary">Common</Badge>
                      )}
                      {type === "appraisal" && (
                        <Badge variant="secondary">Common</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {description}
                    </div>

                    {contingencies.includes(type) && (
                      <div className="mt-4">
                        <FormField
                          control={form.control}
                          name={periodField}
                          render={({ field }) => (
                            <div className="flex items-center gap-2">
                              <NumberInput
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                }}
                                onBlur={() => {
                                  onFormChange(periodField, field.value);
                                }}
                                placeholder={defaultPeriod.toString()}
                                className="w-24"
                              />
                              <span className="text-sm text-muted-foreground">
                                days
                              </span>
                              <Tooltip>
                                <TooltipTrigger>
                                  <InfoIcon className="h-4 w-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Typical period: {defaultPeriod} days</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <Switch
                    checked={contingencies.includes(type)}
                    onCheckedChange={(checked) =>
                      handleContingencyToggle(type, checked)
                    }
                  />
                </div>
              ),
            )}
          </div>

          {/* Appraisal Gap Coverage */}
          {contingencies.includes("appraisal") && (
            <FormField
              control={form.control}
              name="appraisal_gap_coverage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Appraisal Gap Coverage</FormLabel>
                  <FormControl>
                    <PriceInput
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        onFormChange("appraisal_gap_coverage", value);
                      }}
                    />
                  </FormControl>
                  <FormDescription>
                    Amount you're willing to pay above appraised value
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
